import { Box, Button, Typography, useColorScheme } from "@mui/joy";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import { LandingSection } from "../../../LandingLayout";
import { FadeIn, PrimaryButton } from "~/common/components";
import { RoutePaths } from "~/common/constants/route-paths";

const PARALLAX_SPEED = 0.15;

export const Hero = () => {
  const navigate = useNavigate();
  const { colorScheme } = useColorScheme();
  const imageRef = useRef<HTMLDivElement>(null);
  const rafId = useRef<number>(0);

  const handleScroll = useCallback(() => {
    cancelAnimationFrame(rafId.current);

    rafId.current = requestAnimationFrame(() => {
      const element = imageRef.current;

      if (!element) return;

      const rect = element.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const offset = (elementCenter - viewportCenter) * PARALLAX_SPEED;

      element.style.transform = `translateY(${offset}px)`;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, [handleScroll]);

  const heroImage =
    colorScheme === "dark" ? "/hero_dark.png" : "/hero_light.png";

  return (
    <LandingSection
      variant="hero"
      sx={{
        py: { xs: 4, sm: 5, md: 6 },
        minHeight: "auto",
        display: "block",
      }}
    >
      {/* Text Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 4, sm: 5, md: 6 },
        }}
      >
        <FadeIn offsetY={20} duration={300} delay={200}>
          <Typography
            level="h1"
            sx={{
              fontWeight: 600,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
                lg: "3.75rem",
              },
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              textWrap: "balance",
            }}
          >
            Делегуйте перевірку реєстру Brandly. Мінус 80% витрат часу
          </Typography>
        </FadeIn>

        <FadeIn offsetY={20} duration={300} delay={300}>
          <Typography
            textColor="text.secondary"
            sx={{
              fontWeight: 400,
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              lineHeight: "175%",
              letterSpacing: "0.15px",
              maxWidth: "64ch",
              textWrap: "balance",
            }}
          >
            Brandly цілодобово відстежує реєстр УКРНОІВІ замість вас. Отримуйте
            нагадування у Telegram і забудьте про ручні перевірки баз даних.
          </Typography>
        </FadeIn>

        {/* CTA Buttons */}
        <FadeIn offsetY={20} duration={300} delay={500}>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
              mt: { xs: 1, md: 2 },
            }}
          >
            <PrimaryButton
              size="lg"
              onClick={() => void navigate(RoutePaths.PRICING)}
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 1.25, md: 1.5 },
                fontSize: { xs: "sm", md: "md" },
                fontWeight: 600,
              }}
            >
              Розпочати
            </PrimaryButton>
            <Button
              size="lg"
              variant="outlined"
              color="neutral"
              sx={{
                px: { xs: 3, md: 4 },
                py: { xs: 1.25, md: 1.5 },
                fontSize: { xs: "sm", md: "md" },
                fontWeight: 600,
                borderColor: "neutral.400",
                "&:hover": {
                  borderColor: "neutral.500",
                  bgcolor: "neutral.100",
                },
                "[data-joy-color-scheme='dark'] &": {
                  borderColor: "neutral.600",
                  "&:hover": {
                    borderColor: "neutral.500",
                    bgcolor: "neutral.800",
                  },
                },
              }}
              onClick={() => void navigate(RoutePaths.PRICING)}
            >
              Подивитись ціни
            </Button>
          </Box>
        </FadeIn>
      </Box>

      {/* Hero Image - Lazy loaded for performance */}
      <Box
        ref={imageRef}
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          borderRadius: { xs: "md", md: "lg" },
          overflow: "hidden",
          willChange: "transform",
          boxShadow:
            colorScheme === "dark"
              ? "0px 4px 10px 1px rgba(255, 255, 255, 0.25)"
              : "0px 4px 10px 1px #00000040",
        }}
      >
        <img
          src={heroImage}
          alt="Brandly - інтерфейс системи моніторингу заявок"
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      </Box>
    </LandingSection>
  );
};
